import { rankItem, RankingInfo } from '@tanstack/match-sorter-utils'
import { Pagination, Table } from 'react-bootstrap'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import SkeletonRow from '../../../../digital-signing/components/SkeletonRow'
import { DisplayImage } from '../../../../widgets/components/General/DisplayImage'
import { InfoCard2 } from '../../../../widgets/components/UI/InfoCard2'
import tooltipIcon from '../../../../../../_metronic/assets/images/svg/Vector.svg'
import { useIntl } from 'react-intl'
import searchIcon from '../../../../../../_metronic/assets/images/svg/data-room/search-icon-grey.svg'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../../hooks/gtmHelpers'
import { useAuth } from '../../../../auth'
import { BuybackMethods } from '../../core/_constants'
import copyicon from '../../../../../../_metronic/assets/images/svg/copy-svg.svg'
import { toast } from 'react-toastify'
import { useDateFormat } from '../../../../../hooks/useDateFormat'
import brandingConfig from '../../../../../../config/brandingConfig'

export default function DataTableForManualBuyback({
  formik,
  setSharesForBuyBackErrors,
  setSharesForBuyBackValues,

  buybackTransactionId,
  sharesForBuyBackValues,
}: {
  formik: any
  setSharesForBuyBackErrors: Dispatch<SetStateAction<{ [key: string]: string }>>
  setSharesForBuyBackValues: Dispatch<SetStateAction<{ [key: string]: string }>>
  buybackTransactionId?: string | null
  sharesForBuyBackValues: { [key: string]: string }
}) {
  const { formatMessage } = useIntl()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const { companyId, currentUser } = useAuth()
  const [loading] = useState<boolean>(false)
  const { getDateValue } = useDateFormat()

  const sharesForBuyBackErrorsRef = useRef<{ [key: string]: string }>({})
  const tempRef = useRef<{ [key: string]: string }>(
    formik.values.shareTransactionData && formik.values.shareTransactionData.length > 0
      ? formik.values.shareTransactionData.reduce((acc: { [key: string]: string }, item: any) => {
        acc[item.shareTransactionId] = item.manualSharesDeducted.toString()
        return acc
      }, {})
      : sharesForBuyBackValues
  )
  const [shareSumExceedError, setShareSumExceedError] = useState<boolean>(false)

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
  }

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  // Define the handleSharesChange function to update the ref instead of state
  const handleSharesChange = useCallback(
    (id: string, value: string, sharecount: number) => {
      tempRef.current[id] = value
      const numericValue = parseInt(value, 10)
      const totalSharesEntered = Object.values(tempRef.current)
        .map((val) => parseInt(val || '0', 10))
        .reduce((acc, curr) => acc + curr, 0)

      // Check individual constraints and total constraints
      if (
        (!buybackTransactionId && numericValue > sharecount) ||
        (buybackTransactionId &&
          Array.isArray(formik.values.shareTransactionData) &&
          formik.values.shareTransactionData.length > 0 &&
          numericValue >
          sharecount +
          formik.values.shareTransactionData.find(
            (shareTransaction: any) => shareTransaction.shareTransactionId === id
          )?.manualSharesDeducted)
      ) {
        sharesForBuyBackErrorsRef.current[id] = 'Buyback shares cannot exceed the number of shares.'
      } else if (numericValue < 0) {
        sharesForBuyBackErrorsRef.current[id] = 'Buyback shares cannot be a negative value.'
      } else if (totalSharesEntered > formik.values.numberOfShares) {
        setShareSumExceedError(true)
        sharesForBuyBackErrorsRef.current[id] =
          'Sum of buyback shares should not exceed the total number of shares intended for buyback.'
      } else {
        sharesForBuyBackErrorsRef.current[id] = ''
        setShareSumExceedError(false)
        setSharesForBuyBackValues((prevValues: any) => ({
          ...prevValues,
          [id]: value,
        }))
      }

      setSharesForBuyBackErrors({ ...sharesForBuyBackErrorsRef.current })
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const commitSharesForBuyBack = (id: string, sharecount: number) => {
    const value = tempRef.current[id] || ''
    const numericValue = parseInt(value || '0', 10)

    const totalSharesEntered = Object.values(tempRef.current)
      .map((val) => parseInt(val || '0', 10))
      .reduce((acc, curr) => acc + curr, 0)

    if (
      numericValue <= sharecount &&
      numericValue > 0 &&
      totalSharesEntered <= parseInt(formik.values.numberOfShares)
    ) {
      setSharesForBuyBackValues((prevValues: any) => ({
        ...prevValues,
        [id]: value,
      }))
    }
  }

  const handleBlur = (id: string, sharecount: number) => {
    commitSharesForBuyBack(id, sharecount)
  }

  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'shareholderName',
        id: 'shareholderName',
        header: 'Name',
        cell: (info) => {
          const row = info.row.original
          return (
            <div>
              <DisplayImage
                imgName={
                  row?.shareholderProfileImage ? row?.shareholderProfileImage?.name : 'dummy'
                }
                height='20px'
                width='20px'
                fit='contain'
                alt='profile'
                className='rounded-circle me-2'
              />
              <span className='ms-2'>{row?.shareholderName}</span>
            </div>
          )
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
      {
        accessorKey: 'shareholderEmail',
        id: 'shareholderEmail',
        header: 'Email',
        cell: (info) => {
          const row = info.row.original
          return <div>{row?.shareholderEmail ?? '-'}</div>
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
      {
        accessorKey: 'shareTransactionId',
        id: 'Transaction ID',
        header: 'Transaction ID',
        cell: (info) => {
          const row = info.row.original
          const transactionId = row?.shareTransactionId ?? '-'
          const formattedTransactionId =
            transactionId !== '-'
              ? `${transactionId.slice(0, 5)}...${transactionId.slice(-4)}`
              : '-'

          const handleCopy = () => {
            navigator.clipboard.writeText(transactionId)
            toast.success('Transaction ID copied to clipboard!')
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{formattedTransactionId}</span>
              {transactionId !== '-' && (
                <img
                  src={copyicon}
                  alt='copy-icon'
                  onClick={handleCopy}
                  style={{ cursor: 'pointer', marginLeft: '8px', width: '16px', height: '16px' }} // Adjust size as needed
                  title='Copy Transaction ID'
                />
              )}
            </div>
          )
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
      {
        accessorKey: 'transactionDate',
        id: 'transactionDate',
        header: 'Transaction Date',
        cell: (info: { row: { original: { transactionDate?: string } } }) => {
          const row = info.row.original
          return <div>{getDateValue(row?.transactionDate!) ?? '-'}</div>
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
      {
        accessorKey: 'numberOfShares',
        id: 'numberOfShares',
        header: 'No Of Shares',
        cell: (info) => {
          const row = info.row.original
          return <div>{row?.numberOfShares ?? '-'}</div>
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
      {
        accessorKey: 'buybackSharesDeducted',
        id: 'sharesForBuyBack',
        header: 'Shares Of Buyback',
        cell: ({ row, table }) => {
          const rowId = row.original.shareTransactionId
          const sharecount = row.original.numberOfShares
          const buybackAmount = formik.values.numberOfShares || 0
          const minSharesPerRow = formik.values.minimumShareDeduction
          const isLifoOrFifo =
            formik.values.buybackMethod.value === BuybackMethods.FIFO ||
            formik.values.buybackMethod.value === BuybackMethods.LIFO
          // Access isBuybackCreate from props

          let remainingShares = buybackAmount
          const rows = table.getRowModel().rows
          let calculatedShareValue = 0

          if (minSharesPerRow && buybackAmount > 0) {
            while (remainingShares > 0) {
              let anyAllocated = false

              for (let i = 0; i < rows.length; i++) {
                const currentRow = rows[i]
                const currentShareCount = currentRow.original.numberOfShares

                let currentAllocatedShares = 0
                if (currentRow.id === row.id) {
                  currentAllocatedShares = calculatedShareValue
                }

                if (remainingShares > 0 && currentShareCount > currentAllocatedShares) {
                  const allocatedShares = Math.min(
                    minSharesPerRow,
                    currentShareCount - currentAllocatedShares,
                    remainingShares
                  )

                  if (allocatedShares > 0) {
                    if (currentRow.id === row.id) {
                      calculatedShareValue += allocatedShares
                    }
                    remainingShares -= allocatedShares
                    anyAllocated = true
                  }
                }
              }

              // Break if no shares were allocated in the loop to prevent an infinite loop.
              if (!anyAllocated) {
                break
              }
            }
          } else {
            for (let i = 0; i < rows.length; i++) {
              const currentRow = rows[i]
              const currentShareCount = currentRow.original.numberOfShares

              if (remainingShares > 0) {
                const allocatedShares = Math.min(currentShareCount, remainingShares)
                if (currentRow.id === row.id) {
                  calculatedShareValue = allocatedShares
                }
                remainingShares -= allocatedShares
              }
            }
          }

          if (isLifoOrFifo) {
            setSharesForBuyBackValues((prevValues: any) => {
              if (prevValues[rowId] !== calculatedShareValue) {
                return {
                  ...prevValues,
                  [rowId]: calculatedShareValue,
                }
              }
              return prevValues
            })
          }

          return (
            <div>
              <input
                type='number'
                className='form-control h-40px font-size-12'
                value={
                  isLifoOrFifo && calculatedShareValue === 0
                    ? ''
                    : isLifoOrFifo
                      ? calculatedShareValue
                      : tempRef.current[rowId] || ''
                }
                onChange={(e) => handleSharesChange(rowId, e.target.value, sharecount)}
                // onKeyDown={(e) => handleKeyDown(rowId, e, sharecount)}
                onBlur={() => handleBlur(rowId, sharecount)}
                placeholder='Enter Shares Count for Buyback'
                disabled={isLifoOrFifo}
              />
              {sharesForBuyBackErrorsRef.current[rowId] && (
                <div className='text-danger font-size-12'>
                  {sharesForBuyBackErrorsRef.current[rowId]}
                </div>
              )}
            </div>
          )
        },
        filterFn: 'includesString',
        enableSorting: true,
      },
    ],
    [formik?.values?.buybackMethod?.value] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const emailMatch = rankItem(row.original.shareholderEmail, value)
    const nameMatch = rankItem(`${row.original.shareholderName} `, value)

    let bestMatch: RankingInfo | undefined
    if (emailMatch.passed && nameMatch.passed) {
      bestMatch = emailMatch.rank < nameMatch.rank ? emailMatch : nameMatch
    } else if (emailMatch.passed) {
      bestMatch = emailMatch
    } else if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    return !!bestMatch
  }

  const sortedAndFilteredData = useMemo(() => {
    if (buybackTransactionId) {
      return formik.values.shareClassId.shareTransactions.shareTransactionsDetails.sort(
        (a: any, b: any) => {
          const method = formik?.values?.buybackMethod?.value
          if (method === BuybackMethods.FIFO) {
            // Ascending order (older dates first)
            return new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
          }
          if (method === BuybackMethods.LIFO) {
            // Descending order (newer dates first)
            return new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
          }
          return 0 // If no method is defined, treat as equal
        }
      )
    } else {
      return formik.values.shareClassId.shareTransactions.shareTransactionsDetails
        .filter(({ numberOfShares }: { numberOfShares: number }) => numberOfShares > 0)
        .sort((a: any, b: any) => {
          const method = formik?.values?.buybackMethod?.value
          if (method === BuybackMethods.FIFO) {
            // Ascending order (older dates first)
            return new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime()
          }
          if (method === BuybackMethods.LIFO) {
            // Descending order (newer dates first)
            return new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
          }
          return 0 // If no method is defined, treat as equal
        })
    }
  }, [buybackTransactionId, formik.values])

  const table = useReactTable({
    data: sortedAndFilteredData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: false,
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const pageCount = Math.ceil(table.getPrePaginationRowModel().rows.length / pagination.pageSize)
  const currentPage: number = table.getState().pagination.pageIndex;


  const handlePageChange = (pageIndex: number) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex,
    }))
  }

  return (
    <>
      <InfoCard2
        title={formatMessage({
          id: 'Manually Select Shareholders for Share Debits in Buyback Transactions',
        })}
        desc={formatMessage({
          id: 'In this section, choose specific shareholders from whom shares will be debited in this buyback transaction. Enter the share quantity to debit for each selected account. This feature is applicable only when "Manual Selection" is chosen in the Buyback Method dropdown on the “Buyback Details” tab.',
        })}
        slug={brandingConfig.helpLinks.addBuybackShare}
        className='w-100 mb-0'
      />
      <div className='buyback-info d-flex align-items-center p-4 h-25 mb-3'>
        <img src={tooltipIcon} className='me-3' alt='tooltipicon' />
        <div className='buyback-info-text'>
          {`You have entered a ${formik.values.numberOfShares
            } share buyback using the ${formik.values.buybackMethod?.value?.toUpperCase()} Method. the share count for individual transaction should not exceed the total number of shares intended for buyback.`}
        </div>
      </div>
      {shareSumExceedError ? (
        <div className='text-danger font-size-16'>
          {formatMessage({
            id: 'Sum of buyback shares should not exceed the total number of shares intended for buyback.',
          })}
        </div>
      ) : (
        ''
      )}
      <>
        <div className='d-flex justify-content-between align-items-center'>
          <h1 className='fs-4 font-size-20'>
            {formatMessage({ id: 'Shareholders in' })} {formik.values.shareClassId.label}
          </h1>
          <div className='d-flex gap-2 align-items-center justify-content-end mb-4'>
            <form className='align-items-center d-flex bg-color-buyback'>
              <span className='svg-icon svg-icon-3 position-absolute ms-2'>
                <img src={searchIcon} alt='Search icon' />
              </span>
              <DebouncedInput
                value={globalFilter ?? ''}
                onChange={(value) => {
                  setGlobalFilter(String(value))
                  if (String(value).length > 0) {
                    TagManager.dataLayer({
                      dataLayer: {
                        event: GTMEvent.BuybackManualMethodSearchSuccessful,
                        action: 'normal',
                        metadata: {
                          company_id: companyId,
                          user_id: currentUser?.userId,
                          email: currentUser?.email,
                          search_value: value,
                        },
                      },
                    })
                  }
                }}
                className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400'
                placeholder='Search by shareholder'
              />
            </form>
          </div>
        </div>
        <div className='bg-white rounded-2 p-4'>
          <Table className='w-100 '>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 w-100 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan} className=' pb-6 pt-4 '>
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className='border-bottom data-table-link-color align-items-center'
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className='pt-4 border-0 align-middle col-md-1 col-12'>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {formik.values.shareClassId.shareTransactions?.shareTransactionsDetails.length > 0 && (
            <div className='w-100 d-md-flex justify-content-between align-items-center py-4 rounded-bottom'>
              <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
                <select
                  aria-label='Default select example'
                  className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
                  style={{ outline: 'none' }}
                  value={pagination.pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value)
                    setPagination((prev) => ({
                      ...prev,
                      pageSize: newSize,
                      pageIndex: 0, // Reset to first page when page size changes
                    }))
                  }}
                >
                  {[10, 20, 50, 100].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>

                <span className='font-size-13 text-center text-md-start'>
                  {formatMessage({
                    id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                      (currentPage + 1) * pagination.pageSize,
                      table.getPrePaginationRowModel().rows.length
                    )} of ${table.getPrePaginationRowModel().rows.length} records`,
                  })}
                </span>
              </div>

              {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
                  {'<<'}
                </Pagination.First>

                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  {'<'}
                </Pagination.Prev>

                {Array.from({ length: pageCount }, (_, i) => (
                  <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}

                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pageCount - 1}
                >
                  {'>'}
                </Pagination.Next>

                <Pagination.Last
                  onClick={() => handlePageChange(pageCount - 1)}
                  disabled={currentPage >= pageCount - 1}
                >
                  {'>>'}
                </Pagination.Last>
              </Pagination> */}
              <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
                <Pagination.First
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 0}
                >
                  {'<<'}
                </Pagination.First>

                <Pagination.Prev
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  {'<'}
                </Pagination.Prev>

                {(() => {
                  const items = [];
                  const totalPages = pageCount;
                  const pageNeighbours = 1; // Number of pages to show on each side of the current page

                  const startPage = Math.max(1, currentPage - pageNeighbours);
                  const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);

                  // Always show the first page
                  items.push(
                    <Pagination.Item
                      key={0}
                      active={currentPage === 0}
                      onClick={() => handlePageChange(0)}
                    >
                      1
                    </Pagination.Item>
                  );

                  // Show left ellipsis if needed
                  if (startPage > 1) {
                    items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                  }

                  // Show middle pages around the current page
                  for (let i = startPage; i <= endPage; i++) {
                    items.push(
                      <Pagination.Item
                        key={i}
                        active={currentPage === i}
                        onClick={() => handlePageChange(i)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    );
                  }

                  // Show right ellipsis if needed
                  if (endPage < totalPages - 1) {
                    items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                  }

                  // Always show the last page
                  if (totalPages > 1) {
                    items.push(
                      <Pagination.Item
                        key={totalPages - 1}
                        active={currentPage === totalPages - 1}
                        onClick={() => handlePageChange(totalPages - 1)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    );
                  }

                  return items;
                })()}

                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pageCount - 1}
                >
                  {'>'}
                </Pagination.Next>

                <Pagination.Last
                  onClick={() => handlePageChange(pageCount - 1)}
                  disabled={currentPage >= pageCount - 1}
                >
                  {'>>'}
                </Pagination.Last>
              </Pagination>

            </div>
          )}
        </div>
      </>
    </>
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
